var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React, { Children, Component } from 'react';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { DataContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, element, eventListener, object, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableRequestEvent, RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { HttpMethod, HttpService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { createAbort, getRequestParameters, isDefined, isEndpointReady, isEqualShallow, isFunction, propagateProps } from '@philips-internal/oswald-ui-utils';
import RequestAPI from './request-api.js';
const RequestContiner = {
    name: '',
};
let Request = class Request extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            abortController: null,
            data: {},
            loading: false,
        };
        this.onSubmit = (event) => {
            this.submit(event.record);
        };
        this.onDelete = () => {
            this.delete();
        };
    }
    get aborted() {
        const { abortController } = this.state;
        return abortController?.signal.aborted ?? false;
    }
    componentDidMount() {
        this.get();
    }
    componentDidUpdate(prevProps) {
        // TODO: optimize.
        if (isEqualShallow(prevProps.parameters, this.props.parameters)) {
            return;
        }
        this.get();
    }
    componentWillUnmount() {
        this.abortOngoingRequest();
    }
    abortOngoingRequest() {
        const { abortController } = this.state;
        abortController?.abort();
    }
    getOptions(withPrimaryKey) {
        const { parameters } = this.props;
        return {
            endpoint: this.prepareEndpoint(withPrimaryKey),
            parameters,
        };
    }
    prepareEndpoint(withPrimaryKey) {
        const { endpoint, primaryKey } = this.props;
        return withPrimaryKey ? endpoint.concat(`/:${primaryKey}`) : endpoint;
    }
    prepareRequest(loading) {
        const abortController = new AbortController();
        this.setState({ abortController, loading });
        return abortController;
    }
    delete() {
        this.abortOngoingRequest();
        const options = this.getOptions(true);
        if (!isEndpointReady(options, this.context)) {
            return;
        }
        const abortController = this.prepareRequest(false);
        HttpService.delete(options.endpoint, getRequestParameters(options, this.context), { abortController })
            .then((response) => this.handleSuccess(response, HttpMethod.DELETE))
            .catch((error) => this.handleError(error, HttpMethod.DELETE))
            .finally(() => this.handleRequestEnd());
    }
    get() {
        this.abortOngoingRequest();
        const options = this.getOptions(true);
        if (!isEndpointReady(options, this.context)) {
            return;
        }
        const { onBeforeRequest } = this.props;
        const abort = createAbort();
        if (isFunction(onBeforeRequest)) {
            onBeforeRequest(new AbortableRequestEvent({
                abort, method: HttpMethod.GET,
            }));
        }
        void abort.promise.then((aborted) => {
            if (aborted) {
                return;
            }
            const abortController = this.prepareRequest(true);
            HttpService.get(options.endpoint, getRequestParameters(options, this.context), { abortController })
                .then((response) => this.handleGetSuccess(response))
                .catch((error) => this.handleError(error, HttpMethod.GET))
                .finally(() => this.handleRequestEnd());
        });
    }
    submit(record) {
        this.abortOngoingRequest();
        const options = this.getOptions(false);
        if (!isEndpointReady(options, this.context)) {
            return;
        }
        const { endpoint, primaryKey } = this.props;
        const hasPK = isDefined(record[primaryKey]);
        const abortController = this.prepareRequest(false);
        const finalEndpoint = hasPK ? endpoint.concat(`/${record[primaryKey]}`) : endpoint;
        const httpFn = (hasPK ? HttpService.put : HttpService.post).bind(HttpService);
        httpFn(finalEndpoint, getRequestParameters(options, this.context), { abortController, body: record })
            .then((response) => this.handleSuccess(response, hasPK ? HttpMethod.PUT : HttpMethod.POST))
            .catch((error) => this.handleError(error, hasPK ? HttpMethod.PUT : HttpMethod.POST))
            .finally(() => this.handleRequestEnd());
    }
    handleAbort(error, method) {
        const { onAbortRequest } = this.props;
        if (!isFunction(onAbortRequest)) {
            return;
        }
        onAbortRequest(new RequestEvent({
            error,
            method,
        }));
    }
    handleError(error, method) {
        if (error.name === 'AbortError') {
            return this.handleAbort(error, method);
        }
        const { onRequestError } = this.props;
        if (!isFunction(onRequestError)) {
            return;
        }
        onRequestError(new RequestEvent({
            error,
            method,
        }));
    }
    handleGetSuccess(response) {
        this.setState({ data: response.body ?? {} });
        this.handleSuccess(response, HttpMethod.GET);
    }
    handleRequestEnd() {
        this.setState({ abortController: null, loading: false });
    }
    handleSuccess(response, method) {
        const { onAfterRequest } = this.props;
        if (!isFunction(onAfterRequest)) {
            return;
        }
        onAfterRequest(new RequestEvent({
            method,
            response,
        }));
    }
    render() {
        const { children, uuid } = this.props;
        if (Children.count(children) === 0) {
            return React.createElement(Container, { className: "u-expand", uuid: uuid });
        }
        const { data, loading } = this.state;
        return propagateProps(children, {
            data,
            loading,
            onDelete: this.onDelete,
            onSubmit: this.onSubmit,
        });
    }
};
Request.contextType = DataContext.Context;
Request.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    endpoint: string,
    onAbortRequest: eventListener,
    onAfterRequest: eventListener,
    onBeforeRequest: eventListener,
    onRequestError: eventListener,
    parameters: object.notEnumerable(),
    primaryKey: string,
    uuid: string,
};
Request.defaultProps = {
    endpoint: '/api/',
    parameters: {},
    primaryKey: 'id',
};
Request = __decorate([
    AwesomeComponent({ api: RequestAPI, containers: [RequestContiner], type: ComponentType.BEHAVIOR })
], Request);
export default Request;
//# sourceMappingURL=request.js.map